import './DeviceView.css';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  EdgeId,
  IntentRule,
  PathLookupDeviceDecisionsMap,
  PathLookupGraphPreset,
  SnapshotId,
  StpInstance,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { CollapsibleBottomBar } from 'components/CollapsibleBottomBar/CollapsibleBottomBar';
import { PortalToContainer } from 'components/PortalToContainer/PortalToContainer';
import {
  type FC,
  type ReactElement,
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DeviceViewContent } from './DeviceViewContent/DeviceViewContent';
import {
  DeviceViewContentContext,
  type DeviceViewContentContextProps,
} from './DeviceViewContentContext';
import { DeviceViewHeader } from './DeviceViewHeader';
import { DeviceViewWindows } from './DeviceViewWindows/DeviceViewWindows';
import type {
  DeviceViewEntityId,
  PathInspectorPackets,
  PathLookupPathInspectorSelectedPackets,
} from './deviceViewTypes';
import { getActiveDeviceViewEntityId } from './helpers/getActiveDeviceViewEntityId';
import {
  DeviceViewContext,
  type DeviceViewModel,
  useDeviceViewContextSelectors,
} from './model/DeviceViewModel';
import { closeDeviceViewEntity } from './model/commands/closeDeviceViewEntity';
import { selectDetachedDeviceViewEntities } from './model/selectors/selectDetachedDeviceViewEntities';

interface DeviceViewProps {
  decisions?: PathLookupDeviceDecisionsMap | null;
  disableDetach?: boolean;
  disableSiteLink?: boolean;
  hoveredPathInspectorEdge?: EdgeId | null;
  intentRule?: IntentRule | null;
  isolateStpInstances?: boolean;
  onReloadParentTable?: () => void;
  panelClassName?: string;
  pathInspectorPackets?: PathInspectorPackets | null;
  pathLookupPathInspectorSelectedPackets?: PathLookupPathInspectorSelectedPackets;
  pathLookupPreset?: PathLookupGraphPreset;
  showLoader?: boolean;
  // Snapshot ID to load data for devices detail tab or tables.
  // Graph should pass snapshot ID of loaded graph to prevent data refresh
  // when graph isn't loaded after snapshot change.
  snapshotId: SnapshotId | null;
  snapshotToCompareId?: SnapshotId | null;
  stpInstances?: StpInstance[];
  windowContainer?: HTMLElement | null;
}

interface ContainerHeightProps {
  container: HTMLElement | null;
  children: (height: number) => ReactElement;
}

const ContainerHeight: FC<ContainerHeightProps> = (props) => {
  const [height, setHeight] = useState<number>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire only on mount
  useEffect(() => {
    if (props.container) {
      setHeight(
        window.innerHeight <
          props.container.offsetHeight +
            props.container.getBoundingClientRect().top
          ? window.innerHeight - props.container.getBoundingClientRect().top
          : props.container.offsetHeight,
      );
    }
  }, []);

  if (!height) {
    return null;
  }

  return props.children(height);
};

const BareDeviceView: FC<DeviceViewProps> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    activeDeviceViewEntityId: prop('activeDeviceViewEntityId'),
    detachedDeviceViewEntities: selectDetachedDeviceViewEntities,
    deviceViewContainer: prop('deviceViewContainer'),
    deviceViewEntities: prop('deviceViewEntities'),
    deviceViewOpen: prop('deviceViewOpen'),
    deviceViewWindows: prop('deviceViewWindows'),
    dispatch: prop('dispatch'),
    handlersContainer: prop('handlersContainer'),
  });

  const deviceViewEntitiesLength = ctxProps.deviceViewEntities.length;
  const detachedDeviceViewEntitiesLength =
    ctxProps.detachedDeviceViewEntities.length;

  const activeDeviceViewEntityId = getActiveDeviceViewEntityId(
    ctxProps.activeDeviceViewEntityId,
    ctxProps.deviceViewEntities,
    ctxProps.detachedDeviceViewEntities,
  );

  const handleClose = useStableCallback(
    (deviceViewEntityId: DeviceViewEntityId | undefined) => {
      const deviceViewEntity = ctxProps.deviceViewEntities.find(
        (entity) => entity.id === deviceViewEntityId,
      );
      ctxProps.dispatch(closeDeviceViewEntity, {
        deviceViewEntityId,
        deviceViewElement: deviceViewEntity?.deviceViewElement,
      });
    },
  );

  const handleBarClick = useStableCallback(() => {
    const deviceViewEntity = ctxProps.deviceViewEntities.find(
      (entity) => entity.id === activeDeviceViewEntityId,
    );
    if (deviceViewEntity !== undefined && activeDeviceViewEntityId !== null) {
      ctxProps.handlersContainer.handleBarActivate?.({
        deviceViewEntityId: activeDeviceViewEntityId,
        deviceViewElement: deviceViewEntity.deviceViewElement,
      });
    }
  });

  const contentValues: DeviceViewContentContextProps = useMemo(
    (): DeviceViewContentContextProps => ({
      decisions: props.decisions,
      disableSiteLink: props.disableSiteLink,
      hoveredPathInspectorEdge: props.hoveredPathInspectorEdge,
      intentRule: props.intentRule ?? null,
      isolateStpInstances: props.isolateStpInstances,
      onReloadParentTable: props.onReloadParentTable,
      pathInspectorPackets: props.pathInspectorPackets,
      pathLookupPathInspectorSelectedPackets:
        props.pathLookupPathInspectorSelectedPackets,
      pathLookupPreset: props.pathLookupPreset,
      showLoader: !!props.showLoader,
      snapshotId: props.snapshotId,
      snapshotToCompareId: props.snapshotToCompareId,
      stpInstances: props.stpInstances,
    }),
    [
      props.decisions,
      props.disableSiteLink,
      props.hoveredPathInspectorEdge,
      props.intentRule,
      props.isolateStpInstances,
      props.onReloadParentTable,
      props.pathInspectorPackets,
      props.pathLookupPathInspectorSelectedPackets,
      props.pathLookupPreset,
      props.showLoader,
      props.snapshotId,
      props.snapshotToCompareId,
      props.stpInstances,
    ],
  );

  if (!ctxProps.deviceViewEntities.length) {
    return null;
  }

  return (
    <DeviceViewContentContext.Provider value={contentValues}>
      <>
        {props.windowContainer && (
          <PortalToContainer container={props.windowContainer}>
            <DeviceViewWindows />
          </PortalToContainer>
        )}
        {ctxProps.deviceViewOpen && activeDeviceViewEntityId !== null && (
          <PortalToContainer container={ctxProps.deviceViewContainer}>
            <div className={classNames('device-view', props.panelClassName)}>
              <ContainerHeight container={ctxProps.deviceViewContainer}>
                {(height: number) => (
                  <CollapsibleBottomBar
                    container={ctxProps.deviceViewContainer}
                    content={() => (
                      <DeviceViewContent
                        activeDeviceViewEntityId={activeDeviceViewEntityId}
                        nameSuffix='detail'
                      />
                    )}
                    contentClassName='flex min-h-0 flex-col'
                    defaultHeight={Math.round(height / 3)}
                    dragElementClassName='device-view-collapsible-bottom-bar__drag'
                    header={({ onClose, isDragging }) => (
                      <DeviceViewHeader
                        activeDeviceViewEntityId={activeDeviceViewEntityId}
                        detachedDeviceViewEntities={
                          ctxProps.detachedDeviceViewEntities
                        }
                        disableDetach={props.disableDetach}
                        isDragging={isDragging}
                        onClose={onClose}
                      />
                    )}
                    hideOnClose={
                      deviceViewEntitiesLength -
                        detachedDeviceViewEntitiesLength ===
                      1
                    }
                    onClick={handleBarClick}
                    onClose={handleClose}
                    open
                    storagePrefixName='device-view'
                    wrapperClassName='device-view-collapsible-bottom-bar'
                  />
                )}
              </ContainerHeight>
            </div>
          </PortalToContainer>
        )}
      </>
    </DeviceViewContentContext.Provider>
  );
};

interface DeviceViewWrapperProps extends DeviceViewProps {
  deviceView: DeviceViewModel;
}

const DeviceViewWrapper: FC<DeviceViewWrapperProps> = ({
  deviceView,
  ...otherProps
}) => (
  <DeviceViewContext.Provider value={deviceView}>
    <BareDeviceView {...otherProps} />
  </DeviceViewContext.Provider>
);

export const DeviceView = memo(DeviceViewWrapper);
